import type { CrewPostLinks } from '@watchlist/shared';
import type { CrewFeedItem } from './crewFeedUtils';
import { bundleIdFromFeedItem } from './crewFeedUtils';

// ─── SocialPost types ──────────────────────────────────────────────────────

export type SocialPostKind = 'review' | 'customPost' | 'watchlist' | 'bundle';

export interface SocialPostReviewPreview {
  movieId: string;
  movieTitle: string;
  moviePoster?: string;
  movieYear?: string;
  movieGenres?: string[];
  rating?: number;
  reviewText: string;
  updatedDate?: string;
  watchedDate?: string;
  watchedDatePrecision?: 'day' | 'month' | 'year';
}

export interface SocialPostCustomPreview {
  title: string;
  subtitle?: string;
  links: CrewPostLinks;
  isPinned?: boolean;
}

export interface SocialPostWatchlistPreview {
  watchlistId: string;
  name: string;
  description?: string;
  moviePosters?: string[];
  movieCount?: number;
}

export type BundleItem =
  | { kind: 'movie'; movieId: string; title: string; poster?: string; year?: string; genre?: string; rating?: number; watchedDate?: string; watchedDatePrecision?: 'day' | 'month' | 'year' };

export interface SocialPostBundlePreview {
  activityKind: 'movie_watched' | 'movie_added';
  headerText: string;
  items: BundleItem[];
}

export interface SocialPostData {
  kind: SocialPostKind;
  /** reviewId | crewPosts docId | watchlistId | bundleId */
  id: string;
  authorUid: string;
  createdAt: number;

  likeCount: number;
  likedBy: string[];
  commentCount: number;

  review?: SocialPostReviewPreview;
  customPost?: SocialPostCustomPreview;
  watchlist?: SocialPostWatchlistPreview;
  bundle?: SocialPostBundlePreview;
}

// ─── Builders ──────────────────────────────────────────────────────────────

/**
 * Build a SocialPostData of kind 'review' from a review object as found across
 * MovieDetail / UserProfile / OtherUserProfile / UserAllReviewsPage / AllReviewsPage.
 *
 * Each callsite has its own shape (some have `userId`, some have `uid`; some have `likes`,
 * some have `reviewLikes`) — `review` is `any` so the function tolerates all of them.
 */
export function reviewToSocialPost(review: any): SocialPostData {
  const id =
    review.id ||
    (review.userId && review.movieId ? `${review.userId}_${review.movieId}` : '') ||
    (review.uid && review.movieId ? `${review.uid}_${review.movieId}` : '');

  const likedBy: string[] = review.likedBy || review.reviewLikedBy || [];
  const likeCount: number = review.likes ?? review.reviewLikes ?? likedBy.length ?? 0;
  const commentCount: number = review.comments ?? review.reviewCommentCount ?? 0;
  const createdAtMs = (() => {
    // updatedAt is the raw Firestore field; updatedDate is the pre-resolved ISO from getMovieReviews
    const v = review.updatedDate || review.updatedAt || review.dateLabel || review.createdAt;
    if (!v) return Date.now();
    if (typeof v === 'number') return v;
    const t = new Date(v).getTime();
    return Number.isFinite(t) ? t : Date.now();
  })();

  const resolvedUpdatedDate = review.updatedDate || review.updatedAt || review.dateLabel;
  const resolvedWatchedDate = review.watchedDate || review.watchedDateLabel;
  console.log(`[reviewToSocialPost] date fields for ${id}:`, {
    updatedDate: review.updatedDate,
    updatedAt: review.updatedAt,
    watchedDate: review.watchedDate,
    date: review.date,
    createdAt: review.createdAt,
    createdAtMs,
    resolvedUpdatedDate,
    resolvedWatchedDate,
  });

  return {
    kind: 'review',
    id,
    authorUid: review.userId || review.uid || '',
    createdAt: createdAtMs,
    likeCount,
    likedBy,
    commentCount,
    review: {
      movieId: review.movieId,
      movieTitle: review.movieTitle || review.Title || 'Film',
      moviePoster: review.moviePoster || review.Poster,
      movieYear: review.movieYear || review.Year || review.year,
      movieGenres: Array.isArray(review.movieGenres)
        ? review.movieGenres
        : typeof review.movieGenres === 'string'
        ? [review.movieGenres]
        : typeof review.movieGenre === 'string'
        ? [review.movieGenre]
        : undefined,
      rating: typeof review.rating === 'number' ? review.rating : undefined,
      reviewText: review.review || '',
      updatedDate: resolvedUpdatedDate,
      watchedDate: resolvedWatchedDate,
      watchedDatePrecision: review.watchedDatePrecision,
    },
  };
}

/** Build SocialPostData for a custom Watchlist Crew post from its feed item. */
export function customPostToSocialPost(item: CrewFeedItem): SocialPostData {
  const cp = item.crewPost!;
  return {
    kind: 'customPost',
    id: cp.postDocId,
    authorUid: item.actorId,
    createdAt: item.createdAt,
    likeCount: cp.likeCount ?? 0,
    likedBy: cp.likedBy ?? [],
    commentCount: cp.commentCount ?? 0,
    customPost: {
      title: cp.title,
      subtitle: cp.subtitle,
      links: cp.links,
      isPinned: cp.isPinned,
    },
  };
}

/**
 * Build SocialPostData for a watchlist_created feed row OR a direct WatchlistDetail-page open.
 * Engagement counts are sourced from the live `watchlists/{id}` doc that the caller has already
 * loaded. The likedBy array should be the result of `socialService.getWatchlistLikers(id)`.
 */
export function watchlistToSocialPost(args: {
  watchlistId: string;
  authorUid: string;
  createdAtMs: number;
  name: string;
  description?: string;
  moviePosters?: string[];
  movieCount?: number;
  followerCount: number;
  likedBy: string[];
  commentCount: number;
}): SocialPostData {
  return {
    kind: 'watchlist',
    id: args.watchlistId,
    authorUid: args.authorUid,
    createdAt: args.createdAtMs,
    likeCount: args.followerCount,
    likedBy: args.likedBy,
    commentCount: args.commentCount,
    watchlist: {
      watchlistId: args.watchlistId,
      name: args.name,
      description: args.description,
      moviePosters: args.moviePosters,
      movieCount: args.movieCount,
    },
  };
}

/**
 * Build SocialPostData for a movie-activity bundle (movie_watched / movie_added).
 * Engagement is sourced from `socialBundles/{bundleId}` — pass zeros if the doc doesn't exist yet.
 */
export function bundleToSocialPost(
  item: CrewFeedItem,
  engagement: { likeCount: number; likedBy: string[]; commentCount: number }
): SocialPostData | null {
  if (item.kind !== 'movie_watched' && item.kind !== 'movie_added') return null;
  const bundleId = bundleIdFromFeedItem(item);
  if (!bundleId) return null;

  const items: BundleItem[] = item.activities
    .filter(a => !!a.movieId)
    .map(a => ({
      kind: 'movie',
      movieId: a.movieId as string,
      title: a.metadata?.movieTitle || 'Film',
      poster: a.metadata?.moviePoster as string | undefined,
      year: a.metadata?.movieYear as string | undefined,
      genre: a.metadata?.movieGenre as string | undefined,
      rating: typeof a.rating === 'number' ? a.rating : undefined,
      watchedDate: (a.metadata as any)?.watchedDate as string | undefined,
      watchedDatePrecision: (a.metadata as any)?.watchedDatePrecision as 'day' | 'month' | 'year' | undefined,
    }));

  const headerText =
    item.kind === 'movie_added'
      ? (item.count > 1 ? `wants to watch ${item.count} films` : 'wants to watch')
      : (item.count > 1
        ? `${item.activities.some(x => x.rating != null) ? 'rated' : 'watched'} ${item.count} films`
        : (item.activities[0]?.rating != null ? 'rated' : 'watched'));

  return {
    kind: 'bundle',
    id: bundleId,
    authorUid: item.actorId,
    createdAt: item.createdAt,
    likeCount: engagement.likeCount,
    likedBy: engagement.likedBy,
    commentCount: engagement.commentCount,
    bundle: {
      activityKind: item.kind,
      headerText,
      items,
    },
  };
}
